import { useState } from "react"
import { deleteJson, postJson } from "@/lib/api"
import { captureEvent } from "@/lib/analytics"
import type { FeedAssignmentPayload, ResearchAgentAssignmentPayload } from "@/types/researchAgents"

export function FeedAgentAssignments({ payload }: { payload: FeedAssignmentPayload }) {
  const [assignments, setAssignments] = useState(payload.assignments)
  const [selectedId, setSelectedId] = useState<number | "">(payload.agents[0]?.id ?? "")
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const assignedIds = new Set(assignments.map((item) => item.researchAgentId))
  const available = payload.agents.filter((agent) => !assignedIds.has(agent.id))

  async function addAgent() {
    if (selectedId === "" || !payload.canManageAssignments) return
    setBusy(true)
    setError(null)
    try {
      const created = await postJson<ResearchAgentAssignmentPayload>(payload.assignPath, {
        research_agent_id: selectedId,
        auto_run: true,
      })
      setAssignments((current) => [...current, created])
      captureEvent("research_agent_assigned", { feed_id: payload.feedId, research_agent_id: selectedId })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add that agent.")
    } finally {
      setBusy(false)
    }
  }

  async function removeAgent(assignment: ResearchAgentAssignmentPayload) {
    if (!payload.canManageAssignments) return
    setBusy(true)
    setError(null)
    try {
      await deleteJson(assignment.path)
      setAssignments((current) => current.filter((item) => item.id !== assignment.id))
      captureEvent("research_agent_unassigned", { feed_id: payload.feedId, research_agent_id: assignment.researchAgentId })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove that agent.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="font-bold text-lg">Research agents</h3>
      <p className="text-sm text-gray-600">
        Personas attached here run on new matching videos and can be launched from a feed item.
      </p>
      {assignments.length === 0 ? (
        <p className="text-sm text-gray-500">No agents attached yet.</p>
      ) : (
        <ul className="space-y-2">
          {assignments.map((assignment) => (
            <li key={assignment.id} className="flex items-center justify-between gap-2 rounded border border-gray-200 px-3 py-2 text-sm">
              <span>
                {assignment.name}
                {assignment.autoRun ? <span className="ml-2 text-xs text-green-700">auto-run</span> : null}
              </span>
              {payload.canManageAssignments ? (
                <button type="button" onClick={() => void removeAgent(assignment)} className="text-red-700 hover:underline" disabled={busy}>
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {payload.canManageAssignments && available.length > 0 ? (
        <div className="flex gap-2">
          <select
            className="flex-1 rounded-md border-gray-300 text-sm"
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value ? Number(event.target.value) : "")}
          >
            {available.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void addAgent()}
            disabled={busy || selectedId === ""}
            className="rounded-lg bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      ) : null}
      {payload.limits ? (
        <p className="text-xs text-gray-500">
          {payload.limits.maxAssignments == null
            ? "Unlimited assignments"
            : `${assignments.length} / ${payload.limits.maxAssignments} on your plan`}
        </p>
      ) : null}
      {error ? <p className="text-sm text-red-700">{error}</p> : null}
    </div>
  )
}
